import { formatCustomerAddress, type CustomerAddressFields } from '../formatCustomerAddress'

// Mirrors the suffix-stripping approach already proven against real CI-parsed
// company names in src/pages/Jobs/AutoJobs/AddressValidationStep.tsx, kept
// client-side (rather than RPC-ing into the DB's normalize_company_name) so
// this page stays as instant-feeling as HSCodeSearch.
const COMPANY_SUFFIXES = /\s+(CORPORATION|LIMITED|CORP|LTD|CO|INC|LLC|GMBH|AG|SA|LLP|SARL|BV|NV|SL|SPA|EIRL)\.?\s*$/i

export function stripCompanySuffix(name: string): string {
  return name.replace(COMPANY_SUFFIXES, '').trim()
}

export function normalizeForMatch(raw: string | null | undefined): string {
  if (!raw) return ''
  return stripCompanySuffix(raw)
    .toUpperCase()
    .replace(/[.,()'"-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function tokenize(normalized: string): string[] {
  return normalized.split(' ').filter(t => t.length >= 2)
}

/** True if every significant token of `needleRaw` appears as a substring somewhere
 *  in `haystackRaw`'s normalized form. Used for both name and address containment
 *  checks — the CI's shipper/consignee fields are free text that may embed the
 *  Job-side customer's name/address among other content, not an exact match. */
export function containsAllTokens(needleRaw: string | null | undefined, haystackRaw: string | null | undefined): boolean {
  const needleTokens = tokenize(normalizeForMatch(needleRaw))
  if (needleTokens.length === 0) return true
  const haystack = normalizeForMatch(haystackRaw)
  if (!haystack) return false
  return needleTokens.every(t => haystack.includes(t))
}

/** Name-only comparison (see containsAllTokens for the containment semantics). */
export function compareNames(jobName: string | null | undefined, ciName: string | null | undefined): 'match' | 'mismatch' {
  return containsAllTokens(jobName, ciName) ? 'match' : 'mismatch'
}

const ADDRESS_OVERLAP_THRESHOLD = 0.7

/** Splits on commas/whitespace, lowercases, and strips punctuation -- used for the
 *  address token-overlap comparison below (unrelated to tokenize(), which feeds the
 *  stricter substring-containment check used for names). */
function addressTokenSet(raw: string | null | undefined): Set<string> {
  if (!raw) return new Set()
  return new Set(
    raw
      .toLowerCase()
      .replace(/[.,()'"-]+/g, ' ')
      .split(/\s+/)
      .filter(t => t.length > 0)
  )
}

export interface AddressOverlap {
  overlapPct: number
  matched: boolean
}

/** Real addresses on Job vs. CI rarely match verbatim -- one may include a street/district
 *  the other omits, so a strict substring-containment check (see containsAllTokens) was
 *  failing genuine matches. Token overlap tolerates that: pass once the shorter side's
 *  tokens are >= 70% covered by the other side. */
export function addressTokenOverlap(aRaw: string | null | undefined, bRaw: string | null | undefined): AddressOverlap {
  const aTokens = addressTokenSet(aRaw)
  const bTokens = addressTokenSet(bRaw)
  const [shorter, longer] = aTokens.size <= bTokens.size ? [aTokens, bTokens] : [bTokens, aTokens]
  if (shorter.size === 0) return { overlapPct: 0, matched: false }
  let overlapCount = 0
  for (const t of shorter) {
    if (longer.has(t)) overlapCount++
  }
  const ratio = overlapCount / shorter.size
  return { overlapPct: Math.round(ratio * 100), matched: ratio >= ADDRESS_OVERLAP_THRESHOLD }
}

/** Address-only comparison. Returns 'na' (not 'mismatch') when either side has no
 *  address data captured at all -- a missing address isn't a verified mismatch. */
export function compareAddresses(jobAddressTokens: string | null | undefined, ciAddress: string | null | undefined): { status: 'match' | 'mismatch' | 'na'; overlapPct?: number } {
  const jobHas = !!jobAddressTokens?.trim()
  const ciHas = !!ciAddress?.trim()
  if (!jobHas || !ciHas) return { status: 'na' }
  const { overlapPct, matched } = addressTokenOverlap(jobAddressTokens, ciAddress)
  return { status: matched ? 'match' : 'mismatch', overlapPct }
}

export interface PartyComparison {
  nameStatus: 'match' | 'mismatch'
  addressStatus: 'match' | 'mismatch' | 'na'
  // Overall roll-up: fails only if name mismatches or address genuinely mismatches --
  // an 'na' address (not captured on either side) is non-blocking.
  status: 'match' | 'mismatch'
  nameNote?: string
  addressNote?: string
}

/** Single entry point for runShipperCheck/runImporterCheck -- avoids duplicating the
 *  name+address comparison and note-building logic in both. `fieldLabel` (e.g.
 *  'shipper', 'consignee') is used only for the generated note text. */
export function comparePartyRow(
  jobName: string | null | undefined,
  jobAddressTokens: string | null | undefined,
  ciName: string | null | undefined,
  ciAddress: string | null | undefined,
  fieldLabel: string,
): PartyComparison {
  const nameStatus = compareNames(jobName, ciName)
  const { status: addressStatus, overlapPct } = compareAddresses(jobAddressTokens, ciAddress)
  const status = nameStatus === 'match' && addressStatus !== 'mismatch' ? 'match' : 'mismatch'
  const addressNote = addressStatus === 'mismatch'
    ? `address not found in CI ${fieldLabel} field (${overlapPct}% token overlap, needs 70%)`
    : addressStatus === 'match'
      ? `${overlapPct}% address token overlap`
      : undefined
  return {
    nameStatus,
    addressStatus,
    status,
    nameNote: nameStatus === 'mismatch' ? `name not found in CI ${fieldLabel} field` : undefined,
    addressNote,
  }
}

export interface AddressFacets {
  name: string
  // The fully-resolved address string (structured customer columns -- see formatCustomerAddress), or null if
  // the customer has no address data.
  address: string | null
}

interface CustomerRow extends CustomerAddressFields {
  name: string
}

export function normalizeCustomerFacets(customer: CustomerRow): AddressFacets {
  return {
    name: customer.name,
    address: formatCustomerAddress(customer),
  }
}

/** Address tokens joined into one string for a single containsAllTokens()
 *  call against the CI's free-text field. */
export function addressTokenString(facets: AddressFacets): string {
  return facets.address ?? ''
}
